import { useEffect, useLayoutEffect, useRef } from "react";
import { useReducedMotion } from "motion/react";
import { reactionForCups } from "./machine";
import PortraitStage from "./PortraitStage";
import { useCoffeeMachine } from "./useCoffeeMachine";
import "./MiniRelCoffee.css";

function CupIcon() {
  return (
    <svg className="pour-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 10h8.2v4.2a3.2 3.2 0 0 1-3.2 3.2H9.2A3.2 3.2 0 0 1 6 14.2V10h1z" />
      <path d="M15.2 11.2h1.15a2.15 2.15 0 0 1 0 4.3H15.2" />
      <path d="M8.4 7.6c.35-.9.35-1.45 0-2.2" />
      <path d="M11.6 7.6c.35-.9.35-1.45 0-2.2" />
    </svg>
  );
}

type MiniRelCoffeeProps = {
  layout?: "inline" | "embed";
};

export default function MiniRelCoffee({ layout = "inline" }: MiniRelCoffeeProps) {
  const reduced = useReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const {
    phase,
    isPourActive,
    cupFill,
    designerEnergy,
    cupsConsumed,
    announcement,
    busy,
    startPour,
    stopPour,
    pourMeasure,
    reset,
  } = useCoffeeMachine();

  useLayoutEffect(() => {
    if (layout === "embed") return;
    const root = rootRef.current;
    if (!root) return;
    const copy = root.querySelector(".mini-rel-copy");
    if (!(copy instanceof HTMLElement)) return;

    const place = () => {
      copy.style.left = "";
      copy.style.width = "";
      copy.style.transform = "";
    };

    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [layout]);

  useEffect(() => {
    if (layout !== "embed") return;
    const node = rootRef.current;
    if (!node) return;
    const post = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { source: "minirel-coffee", type: "resize", height },
          "*",
        );
      }
    };
    const observer = new ResizeObserver(post);
    observer.observe(node);
    post();
    return () => observer.disconnect();
  }, [layout]);

  const reaction = reactionForCups(cupsConsumed);

  const buttonLabel = isPourActive ? "Pouring" : "Pour coffee";

  let status = "";
  if (isPourActive) status = "Brewing brilliance…";
  else if (phase === "settling" || phase === "sipping") status = "Perfect. Time for a sip.";
  else if (phase === "energized") status = "One moment.";

  const hint = reduced ? "Tap add a measure to pour" : "Press & hold to pour";

  function onPointerDown(event: React.PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0 || busy) return;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* Pour still starts. Cancel, blur, and lost capture stop it. */
    }
    startPour();
  }

  function onPointerUp(event: React.PointerEvent<HTMLButtonElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    stopPour();
  }

  function onLostPointerCapture() {
    stopPour();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.repeat) return;
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    startPour();
  }

  function onKeyUp(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    stopPour();
  }

  return (
    <div
      ref={rootRef}
      className={`mini-rel${layout === "embed" ? " is-embed" : ""}`}
      data-phase={phase}
      data-pouring={isPourActive ? "true" : "false"}
      data-fill={cupFill.toFixed(3)}
      data-energy={designerEnergy}
      data-cups={cupsConsumed}
    >
      <div className="mini-rel-copy">
        <p className="fuel-eyebrow">A little creative fuel — 01</p>
        <h2 className="mini-rel-title">
          Caffeinate
          <br />
          the designer.
        </h2>
        <div className="fuel-rule" aria-hidden="true" />

        <div className="energy" id="energy-status">
          <p className="energy-readout">
            <span className="energy-figure">{designerEnergy}%</span>
            <span className="energy-message">{reaction}</span>
          </p>
          <div
            className="energy-track"
            role="progressbar"
            aria-label="Designer energy"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={designerEnergy}
            aria-valuetext={`${designerEnergy}% ${reaction}`}
          >
            <span
              className="energy-fill"
              style={{ width: `${designerEnergy}%` }}
            />
          </div>
        </div>

        <button
          type="button"
          className="pour-button"
          data-testid="pour-button"
          aria-describedby="pour-hint pour-help"
          aria-pressed={isPourActive}
          disabled={busy}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={stopPour}
          onLostPointerCapture={onLostPointerCapture}
          onBlur={stopPour}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onContextMenu={(event) => event.preventDefault()}
        >
          <span className="pour-label">{buttonLabel}</span>
          <CupIcon />
        </button>
        <p id="pour-hint" className="pour-hint">
          {hint}
        </p>

        <div className="aux-actions">
          <button
            type="button"
            className="text-button"
            data-testid="add-measure"
            onClick={pourMeasure}
            disabled={busy || isPourActive}
          >
            Add a measure
          </button>
          <span className="aux-rule" aria-hidden="true" />
          <button
            type="button"
            className="text-button"
            data-testid="reset-coffee"
            onClick={reset}
          >
            Start over
          </button>
        </div>
      </div>

      <PortraitStage
        phase={phase}
        cupFill={cupFill}
        pouring={isPourActive}
        cupsConsumed={cupsConsumed}
      />

      <p id="pour-help" className="sr-only">
        Press and hold to pour coffee into the mug in front of Mini REL.
        Release to pause. Pouring resumes from the same level. Add a measure
        adds one quarter cup. The mug battery follows the pour. Designer
        energy increases only after she finishes the cup, and it stays at
        100% after the third cup. After each sip she sits up to work. Reset
        returns her to the resting portrait.
      </p>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {status}
      </div>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </div>
  );
}
